import { useEffect, useMemo, useState } from "react";
import { FiSearch } from "react-icons/fi";
import api from "../services/api";

const PER_PAGE_OPTIONS = [10, 20, 50, 100];

const SystemLogsProductPurchase = () => {
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [actionFilter, setActionFilter] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [sortBy, setSortBy] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const [total, setTotal] = useState(0);
  const [lastPage, setLastPage] = useState(1);
  const [summary, setSummary] = useState({
    success: 0,
    warning: 0,
    error: 0,
    critical: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [activeRow, setActiveRow] = useState(null);

  const totalPages = useMemo(() => {
    return lastPage || Math.max(1, Math.ceil(total / perPage));
  }, [lastPage, total, perPage]);

  const handleSearchSubmit = (event) => {
    event.preventDefault();
    setPage(1);
    setQuery(search.trim());
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
  };

  useEffect(() => {
    const fetchSummary = async () => {
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          return;
        }

        const response = await api.get("/system-logs/product-purchase/summary", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            search: query,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
          },
        });

        if (response.data?.status === "success") {
          setSummary(response.data?.data || summary);
        }
      } catch (error) {
        return;
      }
    };

    fetchSummary();
  }, [query, dateFrom, dateTo]);

  useEffect(() => {
    const fetchLogs = async () => {
      setIsLoading(true);
      setLoadError("");
      try {
        const token = localStorage.getItem("rmc2.access_token");
        if (!token) {
          setLoadError("Missing access token.");
          return;
        }

        const response = await api.get("/system-logs/product-purchase", {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            search: query,
            status: statusFilter || undefined,
            action: actionFilter || undefined,
            date_from: dateFrom || undefined,
            date_to: dateTo || undefined,
            sort_by: sortBy,
            sort_dir: sortDir,
            page,
            per_page: perPage,
          },
        });

        if (response.data?.status !== "success") {
          setLoadError("Failed to load logs.");
          return;
        }

        const data = response.data?.data || {};
        setRows(data.rows || []);
        setTotal(data.total || 0);
        setLastPage(data.last_page || 1);
      } catch (error) {
        setLoadError("Failed to load logs.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchLogs();
  }, [
    query,
    statusFilter,
    actionFilter,
    dateFrom,
    dateTo,
    sortBy,
    sortDir,
    page,
    perPage,
  ]);

  const renderPages = () => {
    const buttons = [];
    const start = Math.max(1, page - 2);
    const end = Math.min(totalPages, start + 4);
    for (let p = start; p <= end; p += 1) {
      buttons.push(
        <button
          key={p}
          type="button"
          onClick={() => setPage(p)}
          className={`min-w-[40px] rounded-xl border px-3 py-2 text-sm font-semibold ${
            p === page
              ? "border-[var(--brand-orange)] bg-orange-50 text-[var(--brand-orange-dark)]"
              : "border-slate-200 text-slate-600 hover:border-slate-300"
          }`}
        >
          {p}
        </button>
      );
    }
    return buttons;
  };

  return (
    <div className="mx-auto flex w-full max-w-[1500px] flex-col gap-6">
      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
          System Logs
        </p>
        <h1 className="mt-3 text-3xl font-semibold text-slate-900">
          Product Purchase Logs
        </h1>
        <p className="mt-2 text-sm text-slate-500">
          Monitor product purchase submissions and validation errors.
        </p>
      </div>

      <div className="rounded-3xl border border-white/60 bg-white/90 p-6 shadow-lg shadow-slate-200/60">
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center gap-3">
            <form onSubmit={handleSearchSubmit} className="relative flex-1 min-w-[220px]">
              <FiSearch className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
              <input
                type="text"
                placeholder="Search message or context"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                className="w-full rounded-2xl border border-slate-200 bg-white py-3 pl-11 pr-4 text-sm text-slate-700 placeholder:text-slate-400 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </form>
            <div className="flex items-center gap-3 text-sm text-slate-500">
              <span>Display</span>
              <select
                value={perPage}
                onChange={(event) => {
                  setPerPage(Number(event.target.value));
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-3 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              >
                {PER_PAGE_OPTIONS.map((value) => (
                  <option key={value} value={value}>
                    {value}
                  </option>
                ))}
              </select>
            </div>
            <select
              value={statusFilter}
              onChange={(event) => {
                setStatusFilter(event.target.value);
                setPage(1);
              }}
              className="min-w-[160px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            >
              <option value="">All Status</option>
              {["success", "warning", "error", "critical"].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Action (e.g. purchase.success)"
              value={actionFilter}
              onChange={(event) => {
                setActionFilter(event.target.value);
                setPage(1);
              }}
              className="min-w-[220px] rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-700 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
            />
          </div>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Date From</span>
              <input
                type="date"
                value={dateFrom}
                onChange={(event) => {
                  setDateFrom(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
            <div className="flex flex-col gap-1">
              <span className="text-xs font-semibold text-slate-500">Date To</span>
              <input
                type="date"
                value={dateTo}
                onChange={(event) => {
                  setDateTo(event.target.value);
                  setPage(1);
                }}
                className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm text-slate-600 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>
          </div>
        </div>

        <div className="mt-6 grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          {[
            { label: "Success", value: summary.success },
            { label: "Warning", value: summary.warning },
            { label: "Error", value: summary.error },
            { label: "Critical", value: summary.critical },
          ].map((item) => (
            <div
              key={item.label}
              className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
            >
              <p className="text-xs uppercase tracking-wide text-slate-400">
                {item.label}
              </p>
              <p className="mt-1 text-2xl font-semibold text-slate-700">{item.value}</p>
            </div>
          ))}
        </div>

        {loadError && (
          <div className="mt-4 rounded-2xl border border-rose-100 bg-rose-50 px-4 py-3 text-sm text-rose-600">
            {loadError}
          </div>
        )}

        <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200">
          <div className="w-full overflow-x-auto">
            <table className="min-w-[1200px] w-full text-left text-sm text-slate-600">
              <thead className="bg-[linear-gradient(135deg,var(--brand-orange)_0%,var(--brand-orange-dark)_100%)] text-white">
                <tr>
                  {[
                    { key: "created_at", label: "Created At" },
                    { key: "status", label: "Status" },
                    { key: "action", label: "Action" },
                    { key: "message", label: "Message" },
                    { key: "actor_oauth_user_id", label: "Actor ID" },
                  ].map((column) => (
                    <th
                      key={column.key}
                      onClick={() => handleSort(column.key)}
                      className="cursor-pointer px-4 py-3 text-xs font-semibold"
                    >
                      <div className="flex items-center gap-2">
                        {column.label}
                        {sortBy === column.key && (
                          <span className="text-white">{sortDir === "asc" ? "▲" : "▼"}</span>
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-4 py-3 text-xs font-semibold">Context</th>
                  <th className="px-4 py-3 text-xs font-semibold">Action</th>
                </tr>
              </thead>
              <tbody className="bg-white">
                {isLoading ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={6}>
                      Loading logs...
                    </td>
                  </tr>
                ) : rows.length === 0 ? (
                  <tr>
                    <td className="px-4 py-8 text-center text-sm text-slate-400" colSpan={6}>
                      No logs found.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <tr key={row.id} className="border-t border-slate-100">
                      <td className="px-4 py-3 text-xs text-slate-500">
                        {row.created_at || "-"}
                      </td>
                      <td className="px-4 py-3 font-semibold text-slate-700">
                        {row.status || "-"}
                      </td>
                      <td className="px-4 py-3">{row.action || "-"}</td>
                      <td className="px-4 py-3">{row.message || "-"}</td>
                      <td className="px-4 py-3">{row.actor_oauth_user_id || "-"}</td>
                      <td className="px-4 py-3">
                        <span className="block max-w-[360px] truncate text-xs text-slate-500">
                          {row.context || "-"}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <button
                          type="button"
                          onClick={() => {
                            setActiveRow(row);
                            setIsViewOpen(true);
                          }}
                          className="inline-flex items-center gap-1 rounded-full border border-slate-200 bg-white px-3 py-1 text-xs font-semibold text-slate-600 transition hover:border-slate-300 hover:text-slate-800"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 text-sm text-slate-500">
          <div>
            Showing {rows.length} of {total} entries
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              First
            </button>
            <button
              type="button"
              onClick={() => setPage((prev) => Math.max(1, prev - 1))}
              disabled={page === 1}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Previous
            </button>
            {renderPages()}
            <button
              type="button"
              onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Next
            </button>
            <button
              type="button"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="rounded-xl border border-slate-200 px-3 py-2 text-sm font-semibold text-slate-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Last
            </button>
          </div>
        </div>
      </div>

      {isViewOpen && activeRow && (
        <div className="fixed inset-0 z-50 bg-slate-900/50">
          <div className="h-full w-full overflow-y-auto bg-white p-6 shadow-2xl shadow-slate-900/30">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">
                  System Logs
                </p>
                <h3 className="mt-2 text-2xl font-semibold text-slate-900">
                  Log Details
                </h3>
                <p className="mt-2 text-sm text-slate-500">
                  Full payload and metadata for this log entry.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-full border border-slate-200 px-3 py-1 text-xs font-semibold text-slate-500 hover:border-slate-300 hover:text-slate-700"
              >
                Close
              </button>
            </div>

            <div className="mt-6 grid gap-4 lg:grid-cols-2">
              {[
                { label: "Created At", value: activeRow.created_at },
                { label: "Status", value: activeRow.status },
                { label: "Action", value: activeRow.action },
                { label: "Message", value: activeRow.message },
                { label: "Actor ID", value: activeRow.actor_oauth_user_id },
                { label: "Actor Scope", value: activeRow.actor_scope },
                { label: "IP Address", value: activeRow.ip_address },
                { label: "User Agent", value: activeRow.user_agent },
              ].map((item) => (
                <div
                  key={item.label}
                  className="rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3"
                >
                  <p className="text-xs uppercase tracking-wide text-slate-400">
                    {item.label}
                  </p>
                  <p className="mt-1 text-sm font-semibold text-slate-700">
                    {item.value || "-"}
                  </p>
                </div>
              ))}
            </div>

            <div className="mt-6 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-3">
              <p className="text-xs uppercase tracking-wide text-slate-400">
                Context (JSON)
              </p>
              <pre className="mt-2 whitespace-pre-wrap text-xs text-slate-600">
                {activeRow.context || "-"}
              </pre>
            </div>

            <div className="mt-6 flex justify-end">
              <button
                type="button"
                onClick={() => setIsViewOpen(false)}
                className="rounded-2xl bg-[var(--brand-orange)] px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-orange-200 transition hover:bg-[var(--brand-orange-dark)]"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SystemLogsProductPurchase;
